/**
 * R268ff · Feb 2026 — GST Sales detail dialog + revision timeline.
 *
 * Renders a printable-format preview (mirrors the exported PDF 1:1)
 * plus a collapsible revision-history timeline. Extracted from
 * `GstSales.jsx` to keep the page shell slim.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { History, Printer, Pencil, GitBranch, ShieldCheck, Send, XCircle, Clock, Share2 } from "lucide-react";
import DocumentPreview from "@/components/DocumentPreview";
import { fmtMoney } from "@/lib/currency";
import { StatusPill } from "./shared";
import ReceivablesTiles from "@/components/Client/ReceivablesTiles";
import FulfilmentBars from "./FulfilmentBars";
import AttachmentsPanel from "./AttachmentsPanel";
import ShareLinkDialog from "./ShareLinkDialog";
import useWorkspacePerms from "@/lib/useWorkspacePerms";

const _EVENT_LABEL = {
  created:        { label: "Created",          color: "text-emerald-700 dark:text-emerald-300" },
  edited:         { label: "Edited",           color: "text-blue-700 dark:text-blue-300" },
  status_changed: { label: "Status changed",   color: "text-violet-700 dark:text-violet-300" },
  payment_added:  { label: "Payment recorded", color: "text-emerald-700 dark:text-emerald-300" },
  voided:         { label: "Voided",           color: "text-rose-700 dark:text-rose-300" },
  converted:      { label: "Converted",        color: "text-amber-700 dark:text-amber-300" },
};

function formatValue(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toString();
  if (Array.isArray(v)) return `${v.length} row${v.length === 1 ? "" : "s"}`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  return String(v).slice(0, 80);
}

export function RevisionsTimeline({ revisions, loading }) {
  if (loading) {
    return <div className="text-xs text-muted-foreground py-3">Loading history…</div>;
  }
  if (!revisions?.length) return null;
  return (
    <div data-testid="gst-revisions-timeline">
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
        <History className="h-3 w-3" /> Revision history
      </div>
      <ol className="space-y-2">
        {revisions.map((r) => {
          const meta = _EVENT_LABEL[r.event_type] || { label: r.event_type, color: "" };
          return (
            <li key={r.id}
                data-testid={`gst-revision-${r.revision_no}`}
                className="border border-border rounded p-2 text-xs space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] text-muted-foreground">#{r.revision_no}</span>
                  <span className={`font-semibold ${meta.color}`}>{meta.label}</span>
                  {r.actor_name && (
                    <span className="text-muted-foreground">by {r.actor_name}</span>
                  )}
                </div>
                <span className="text-muted-foreground">{r.at?.slice(0, 16).replace("T", " ")}</span>
              </div>
              {r.note && (
                <div className="italic text-muted-foreground">“{r.note}”</div>
              )}
              {r.changes?.length > 0 && (
                <ul className="pl-2 space-y-0.5">
                  {r.changes.map((c, i) => (
                    <li key={i} className="text-[11px]">
                      <span className="font-medium">{c.field.replace(/_/g, " ")}</span>
                      <span className="text-muted-foreground"> · </span>
                      <span className="line-through text-rose-700 dark:text-rose-300">{formatValue(c.before)}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span className="text-emerald-700 dark:text-emerald-300">{formatValue(c.after)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function DetailDialog({ doc, ws, profile, onClose, onEdit, onOpenDoc, onOpenTask, onPatch }) {
  const [revisions, setRevisions] = useState([]);
  const [revLoading, setRevLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Feb 2026 R293u · Sibling versions for the current estimate's root.
  // Non-estimate docs leave this empty.
  const [versions, setVersions] = useState([]);
  const { can } = useWorkspacePerms();

  useEffect(() => {
    if (!doc || !ws) { setRevisions([]); return; }
    let cancelled = false;
    setRevLoading(true);
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/documents/${doc.id}/revisions`);
        if (!cancelled) setRevisions(r.data.revisions || []);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setRevLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [doc, ws]);

  // Feb 2026 R293u · Load versions for estimates.
  useEffect(() => {
    if (!doc || !ws || doc.doc_type !== "estimate") { setVersions([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/documents/${doc.id}/versions`);
        if (!cancelled) setVersions(r.data.versions || []);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [doc, ws]);

  // Feb 2026 R293ze · Load fulfilment for quotation (SO rollup) and
  // sales order (invoice rollup) so the detail view shows a visual
  // bar for each line without the user opening the picker.
  const [fulfil, setFulfil] = useState(null);
  const [taskFulfil, setTaskFulfil] = useState(null);
  useEffect(() => {
    if (!doc || !ws) { setFulfil(null); return; }
    const canFulfil = doc.doc_type === "estimate" || doc.doc_type === "sales_order";
    if (!canFulfil) { setFulfil(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = doc.doc_type === "estimate"
          ? `/gst/workspaces/${ws}/estimates/${doc.id}/fulfillment`
          : `/gst/workspaces/${ws}/sales-orders/${doc.id}/invoice-fulfillment`;
        const r = await api.get(url);
        if (!cancelled) setFulfil(r.data);
      } catch (e) { /* silent — detail view keeps rendering */ }
    })();
    return () => { cancelled = true; };
  }, [doc, ws]);
  // Feb 2026 R293zg · For SOs also load the task delivery rollup.
  useEffect(() => {
    if (!doc || !ws || doc.doc_type !== "sales_order") { setTaskFulfil(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/gst/workspaces/${ws}/sales-orders/${doc.id}/task-fulfillment`);
        if (!cancelled) setTaskFulfil(r.data);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [doc, ws]);

  if (!doc) return null;
  const canEdit = !["void", "cancelled", "rejected", "converted", "superseded"].includes(doc.status);

  // Feb 2026 R293zq · Approval workflow helpers.
  const APPROVE_PERM = { estimate: "quotation.approve",
                           sales_order: "sales_order.approve",
                           invoice: "invoice.approve" };
  const isApprovable = ["estimate", "sales_order", "invoice"].includes(doc.doc_type);
  const apStatus = doc.approval_status || (isApprovable ? "draft" : null);
  const canApprovePerm = isApprovable && APPROVE_PERM[doc.doc_type]
    && can?.(APPROVE_PERM[doc.doc_type]);
  const canSubmit = isApprovable && (apStatus === "draft" || apStatus === "rejected");
  const canActApprove = canApprovePerm && apStatus === "submitted";
  const canActReject = canApprovePerm && apStatus === "submitted";

  const doSubmit = async () => {
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/submit`);
      toast.success("Submitted for approval");
      onPatch?.(doc.id);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const doApprove = async () => {
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/approve`);
      toast.success("Approved");
      onPatch?.(doc.id);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const doRejectApproval = async () => {
    const reason = window.prompt("Reason for rejection? (optional)") || "";
    try {
      await api.post(`/gst/workspaces/${ws}/documents/${doc.id}/reject`, { reason });
      toast.success("Rejected · back to draft");
      onPatch?.(doc.id);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/gst/workspaces/${ws}/documents/${doc.id}/pdf`,
                                  { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.doc_no || "document"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="w-screen max-w-full h-[100dvh] max-h-[100dvh] rounded-none border-0 overflow-y-auto p-4 sm:max-w-4xl sm:h-auto sm:max-h-[92vh] sm:rounded-lg sm:p-6" data-testid="gst-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {doc.doc_no}
            <StatusPill status={doc.status} approvalStatus={doc.approval_status} />
            {/* Feb 2026 R293zq · Approval status chip + actions. */}
            {isApprovable && apStatus && (
              <span
                className={
                  "inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 " +
                  (apStatus === "approved"
                    ? "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800"
                    : apStatus === "submitted"
                      ? "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800"
                      : apStatus === "rejected"
                        ? "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800"
                        : "bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-900/60 dark:text-slate-200 dark:border-slate-700")
                }
                data-testid="gst-detail-approval-chip"
                title={apStatus === "rejected" && doc.rejection_reason
                  ? `Rejected · ${doc.rejection_reason}`
                  : (apStatus === "approved" && doc.approved_by_name
                      ? `Approved by ${doc.approved_by_name}`
                      : `Approval: ${apStatus}`)}
              >
                {apStatus === "approved" ? <ShieldCheck className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                {apStatus}
              </span>
            )}
            {/* Feb 2026 R293u · Version badge + sibling-version switcher
                for estimates. Only renders when ≥2 versions exist in
                the root so clean v1-only estimates stay quiet. */}
            {doc.doc_type === "estimate" && versions.length > 1 && (
              <div className="inline-flex items-center gap-1 border border-violet-300 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40 rounded px-1.5 py-0.5 max-w-full"
                   data-testid="gst-detail-version-switcher">
                <GitBranch className="h-3 w-3 text-violet-700 dark:text-violet-300 shrink-0" />
                <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-800 dark:text-violet-200 shrink-0">
                  v{doc.version || 1} of {versions.length}
                </span>
                <div className="flex items-center gap-0.5 ml-1 overflow-x-auto no-scrollbar">
                  {versions.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => v.id !== doc.id && onOpenDoc?.(v)}
                      disabled={v.id === doc.id}
                      className={`text-[10px] font-mono px-1 rounded border transition-colors shrink-0 ${
                        v.id === doc.id
                          ? "bg-violet-600 text-white border-violet-600 cursor-default"
                          : "bg-white dark:bg-neutral-900 text-violet-700 dark:text-violet-200 border-violet-300 dark:border-violet-800 hover:bg-violet-100 dark:hover:bg-violet-900"
                      }`}
                      title={`${v.doc_no} v${v.version} · ${v.status}`}
                      data-testid={`gst-detail-version-tab-${v.version}`}
                    >
                      v{v.version}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {doc.is_legacy && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide
                                 text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-950/60
                                 border border-amber-300 dark:border-amber-800 rounded px-1.5 py-0.5"
                       data-testid="gst-detail-legacy-badge"
                       title="Historic invoice imported from another billing system">
                Legacy
              </span>
            )}
            {doc.internal_ref && (
              <span className="text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5"
                       data-testid="gst-detail-internal-ref"
                       title="Acquilix internal reference">
                {doc.internal_ref}
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                        data-testid="gst-detail-toggle-history"
                        onClick={() => setShowHistory((v) => !v)}>
                <History className="h-3 w-3 mr-1" />
                {showHistory ? "Hide" : "Show"} history
                {revisions.length > 0 && (
                  <span className="ml-1 text-muted-foreground">({revisions.length})</span>
                )}
              </Button>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                        data-testid="gst-detail-pdf-btn"
                        onClick={downloadPdf}>
                <Printer className="h-3 w-3 mr-1" /> PDF
              </Button>
              {/* Feb 2026 R293zm · Fulfilment PDF is always available for SOs,
                  regardless of whether tasks have been pushed yet. */}
              {doc.doc_type === "sales_order" && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                          data-testid="gst-detail-fulfilment-pdf-btn"
                          onClick={async () => {
                            try {
                              const r = await api.get(`/gst/workspaces/${ws}/sales-orders/${doc.id}/fulfilment-pdf`, { responseType: "blob" });
                              const blob = new Blob([r.data], { type: "application/pdf" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `${doc.doc_no || "sales-order"}-fulfilment.pdf`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              setTimeout(() => URL.revokeObjectURL(url), 1000);
                            } catch (e) { toast.error(apiErrorMessage(e)); }
                          }}>
                  <Printer className="h-3 w-3 mr-1" /> Fulfilment PDF
                </Button>
              )}
              {canEdit && onEdit && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                          data-testid="gst-detail-edit-btn"
                          onClick={() => onEdit(doc)}>
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
              )}
              {/* Feb 2026 R293zx · Mint a public share link so the
                  customer can open the PDF + attachments without an
                  account. Owner / admin / author only. */}
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                        data-testid="gst-detail-share-btn"
                        onClick={() => setShareOpen(true)}>
                <Share2 className="h-3 w-3 mr-1" /> Share link
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {doc.doc_type.replace("_", " ")} · {doc.doc_date}
            {doc.parent_doc_id && " · converted from parent"}
            {" · This is exactly how it will print."}
          </DialogDescription>
        </DialogHeader>

        {doc.warnings?.length > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 p-2 rounded mb-2">
            {doc.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
          </div>
        )}

        {/* Feb 2026 R293za · Customer receivables snapshot — billed /
            received / outstanding / overdue for this customer across
            every live doc. Helps the user decide whether to extend
            more credit before approving a new quote. */}
        {doc.customer_name && (
          <div className="mb-3" data-testid="gst-detail-receivables">
            <ReceivablesTiles
              ws={ws}
              customerName={doc.customer_name}
              currency={doc.currency || doc.totals?.currency || "INR"}
              compact />
          </div>
        )}

        {/* Feb 2026 R293ze · Fulfilment panel — quotation shows
            SO-rollup bars; SO shows invoice-rollup bars. Hidden
            when there's nothing to fulfil. */}
        {fulfil?.lines?.length > 0 && (
          <div className="mb-3 border border-border rounded p-3 bg-muted/10"
               data-testid="gst-detail-fulfil-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {doc.doc_type === "estimate" ? "Fulfilment · ordered vs. remaining" : "Billing · invoiced vs. remaining"}
              </div>
              {(() => {
                const children = doc.doc_type === "estimate" ? fulfil.child_sales_orders : fulfil.child_invoices;
                return children?.length ? (
                  <div className="flex flex-wrap gap-1" data-testid="gst-detail-fulfil-children">
                    {children.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border bg-background text-[10px]">
                        {c.doc_no}
                        <span className="uppercase tracking-wide text-[9px] text-muted-foreground">{c.status}</span>
                      </span>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>
            <FulfilmentBars
              lines={fulfil.lines}
              fulfilledKey={doc.doc_type === "estimate" ? "qty_ordered" : "qty_invoiced"}
              compact
              testid="gst-detail-fulfil-bars"
            />
          </div>
        )}

        {/* Feb 2026 R293zg · SO delivery overlay — per-line task counts
            + overall progress. Hidden until the user pushes lines to
            tasks for the first time.
            R293zj · Shows BOTH qty-weighted and amount-weighted bars
            so a 10-unit line doesn't count the same as a 1-unit line. */}
        {doc.doc_type === "sales_order" && taskFulfil?.overall_task_count > 0 && (
          <div className="mb-3 border border-border rounded p-3 bg-muted/10"
               data-testid="gst-detail-task-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Delivery · tasks done vs. total
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[11px] font-mono tabular-nums">
                  {taskFulfil.overall_task_done}/{taskFulfil.overall_task_count}
                </div>
              </div>
            </div>
            {/* Dual overall bars: qty % and amount % */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2"
                 data-testid="gst-detail-task-bars-dual">
              <div data-testid="gst-detail-task-qty-bar">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5">
                  <span>By quantity</span>
                  <span className="font-mono tabular-nums">{taskFulfil.overall_qty_percent ?? taskFulfil.overall_percent}%</span>
                </div>
                <div className="w-full bg-muted rounded-sm overflow-hidden" style={{ height: 6 }}>
                  <div className="h-full bg-sky-500 transition-all"
                       style={{ width: `${taskFulfil.overall_qty_percent ?? taskFulfil.overall_percent}%` }} />
                </div>
              </div>
              <div data-testid="gst-detail-task-amount-bar">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5">
                  <span>By amount</span>
                  <span className="font-mono tabular-nums">{taskFulfil.overall_amount_percent ?? taskFulfil.overall_percent}%</span>
                </div>
                <div className="w-full bg-muted rounded-sm overflow-hidden" style={{ height: 6 }}>
                  <div className="h-full bg-violet-500 transition-all"
                       style={{ width: `${taskFulfil.overall_amount_percent ?? taskFulfil.overall_percent}%` }} />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              {taskFulfil.lines.filter((l) => l.task_count > 0).map((l) => (
                <div key={l.id} className="text-xs" data-testid={`gst-detail-task-line-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate flex-1">{l.description}</div>
                    <div className="font-mono text-[10px] tabular-nums text-muted-foreground shrink-0">
                      {l.task_done}/{l.task_count}
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-sm overflow-hidden mt-0.5" style={{ height: 4 }}>
                    <div className={`h-full transition-all ${l.task_percent === 100 ? "bg-emerald-500" : "bg-violet-500"}`}
                         style={{ width: `${l.task_percent}%` }} />
                  </div>
                  {/* Per-task chips — click to open the task */}
                  {l.tasks?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`gst-detail-task-chips-${l.id}`}>
                      {l.tasks.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => onOpenTask?.(t)}
                          className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors hover:bg-primary hover:text-primary-foreground
                            ${t.status === "done"
                              ? "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200"
                              : "bg-background border-border text-muted-foreground"}`}
                          title={`${t.title} · ${t.status}${t.assignee_id ? "" : ""}`}
                          data-testid={`gst-detail-task-chip-${t.id}`}
                        >
                          {t.title?.slice(0, 32) || "task"}
                          <span className="uppercase ml-1 opacity-70">{t.status}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* R268s · Printable-format preview — mirrors the exported PDF 1:1 */}
        <div className="rounded border border-border bg-muted/30 p-3" data-testid="gst-detail-preview">
          <DocumentPreview doc={doc} profile={profile} />
        </div>

        {/* Feb 2026 R293zr · Attachments — the full brief travels with
            the document. Delete is gated by the same canEdit check as
            the Edit button so finalised docs don't lose their files. */}
        <div className="mt-3">
          <AttachmentsPanel
            ws={ws}
            docId={doc.id}
            initial={doc.attachments || []}
            readOnly={!canEdit}
          />
        </div>

        {doc.payments?.length > 0 && (
          <div className="mt-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Payments recorded</div>
            <div className="space-y-1 text-xs">
              {doc.payments.map((p) => (
                <div key={p.id} className="flex justify-between border border-border rounded p-2">
                  <div>
                    <div className="font-medium">
                      {fmtMoney(p.amount, doc.currency || doc.totals?.currency || "INR")} · {p.mode.toUpperCase()}
                    </div>
                    {p.reference && <div className="text-muted-foreground">{p.reference}</div>}
                    {/* R268w · Show remittance detail when the payment
                        landed in a different currency (foreign wire). */}
                    {p.remitted_amount != null && p.remitted_currency && p.remitted_currency !== (doc.currency || "INR") && (
                      <div className="text-[11px] text-emerald-700 dark:text-emerald-300">
                        Remitted {fmtMoney(p.remitted_amount, p.remitted_currency)}
                        {p.bank_charges > 0 && <> · charges {fmtMoney(p.bank_charges, p.remitted_currency)}</>}
                        {p.net_remitted != null && <> · <b>net {fmtMoney(p.net_remitted, p.remitted_currency)}</b></>}
                        {p.fx_rate && <> · @ {Number(p.fx_rate).toFixed(4)}</>}
                      </div>
                    )}
                  </div>
                  <div className="text-muted-foreground">{p.paid_on}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* R268r · Revision history timeline (collapsible) */}
        {showHistory && (
          <div className="mt-3">
            <RevisionsTimeline revisions={revisions} loading={revLoading} />
          </div>
        )}
        {/* Feb 2026 R293zx · Share-by-link dialog */}
        {shareOpen && (
          <ShareLinkDialog
            ws={ws}
            doc={doc}
            onClose={() => setShareOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
